/**
 * Soft≠Live Page174 — Fleet Device residual liquid (page136/74 + status SoT).
 * Mount: /fleet/device/{id}
 * Buses:
 *   fleet device status (always-on Live posture)
 *   devices register (+ detail rows) · pins · ports · board-telemetry
 *   profile write · command ping · custody revoke/delete/factory
 * Soft residual deleted:
 *  - sandpit Device without rail / KPI / posture SoT / Refresh
 *  - Soft invent register when seat gated
 *  - useLive-only register invent when devices list available
 *  - age “−1s ago” invent (honest —)
 *  - Soft custody / place write when seat closed
 *  - unlabeled Show on map / Reset without · Live honesty where Write
 */
import { useState, useEffect, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, KV, Card, Field, Input, Alert,
         SPRING, FADE, go, canonicalGroupKey, sameGroupKey } from "./ui.js";
import { PORTS, PINS } from "./edge-data.js";
import { Board3D, BoardDetail, Silicon, SILICON as SILICON_GEO } from "./node3d.js";
import { Forge } from "./forge.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { mergeLiveSeatsOntoSoftPins } from "./board-seat-adapter.js";
import {
  applyPinTelemetry,
  applySiliconTelemetry,
  poeLiveFromTelemetry,
} from "./board-telemetry-adapter.js";
import {
  geometryPortsSeed,
  mergeLivePortsOntoSoftPorts,
} from "./board-ports-adapter.js";
import { DeviceCamerasLive } from "./device-cameras.js";
import { DeviceCapsLive } from "./device-caps-live.js";
import { EdgeSkillsWarehouseDesk } from "./edge-skills-warehouse.js";
import { DeviceLinkPowerLive } from "./device-link-live.js";
import { DeviceIdentityLive } from "./device-identity-live.js";
import { DeviceOverviewLive } from "./device-overview-live.js";
import { boardLabelSay } from "./operator-say.js";

/** Soft operator errors — never paint wire paths or raw detail codes. */
export function softFleetDeviceErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/confirm_text|exactly match/.test(low)) {
    return "Type the full unit serial exactly to confirm.";
  }
  if (/\b403\b|forbidden|insufficient|not_authorized|device not in|operation denied|cannot unbind|requires owning/.test(low)) {
    return "Your account cannot change this unit. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|lookuperror/.test(low)) {
    return "That unit is not available.";
  }
  if (/\b400\b|invalid|required/.test(low) && !/confirm/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many|quota/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline|revoked/.test(low)) {
    return "This unit is not open to commands right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/fleet\/|http|GET |POST |PATCH |status_path/.test(low)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

export const KINDS_DEVICE = {
  camera:  { t: "Camera", glyph: "camera", tone: "#3DE1C8", shape: "sq" },
  contact: { t: "Door contact", glyph: "door", tone: "#7FD1FF", shape: "rect" },
  siren:   { t: "Siren", glyph: "siren", tone: "#FF9B5C", shape: "tri" },
  gateway: { t: "Mast / gateway", glyph: "mast", tone: "#A78BFA", shape: "hex" },
  sensor:  { t: "Sensor", glyph: "sensor", tone: "#9BE37D", shape: "circ" },
  lidar:   { t: "Lidar", glyph: "lidar", tone: "#FFD166", shape: "oct" },
};
const kindOf = (k) => KINDS_DEVICE[k] || { t: k, glyph: "chip", tone: "#8CA2A6", shape: "circ" };

function guessKind(row) {
  const blob = [
    row && row.display_label, row && row.map_label, row && row.hw_code_resolved,
    row && row.product_hw_code, row && row.board_profile, row && row.chip_target,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(cam|camera|snap|vision|lidar)\b/.test(blob)) return /lidar/.test(blob) ? "lidar" : "camera";
  if (/\b(siren|strobe|horn|alarm)\b/.test(blob)) return "siren";
  if (/\b(door|contact|reed|magnet)\b/.test(blob)) return "contact";
  if (/\b(mast|gateway|bridge|hub)\b/.test(blob)) return "gateway";
  if (/\b(sensor|leak|smoke|pir|temp)\b/.test(blob)) return "sensor";
  return "node";
}

/** Pure: age text — Soft −1s invent forbidden. */
export function fleetDeviceAgeOf(age) {
  if (age == null || !Number.isFinite(Number(age)) || Number(age) < 0) return "—";
  const v = Number(age);
  if (v < 60) return Math.round(v) + "s ago";
  if (v < 3600) return Math.floor(v / 60) + "m ago";
  return Math.floor(v / 3600) + "h ago";
}

/** Pure: flatten devices-register items for fleet device desk. */
export function fleetDeviceRegisterOf(devicesPack) {
  const rows = (devicesPack && (devicesPack.items || devicesPack.devices)) || [];
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((r) => {
    if (!r) return null;
    const id = String(r.device_id || "").trim();
    if (!id) return null;
    const online = !!(r.is_online === true || r.online === true || r.presence === "online"
      || String(r.status || "").toLowerCase() === "online");
    const lat = r.map_lat == null ? null : Number(r.map_lat);
    const lng = r.map_lng == null ? null : Number(r.map_lng);
    const hasPos = Number.isFinite(lat) && Number.isFinite(lng);
    const ageRaw = r.last_signal_age_s != null ? Number(r.last_signal_age_s)
      : (r.age_s != null ? Number(r.age_s) : null);
    return {
      id,
      label: boardLabelSay(r, id),
      kind: guessKind(r),
      zone: String(r.zone || "").trim(),
      site: String(r.site_id || r.site || "").trim(),
      group: String(r.notification_group || "").trim(),
      online,
      offline: !online,
      age: Number.isFinite(ageRaw) && ageRaw >= 0 ? ageRaw : null,
      ll: hasPos ? [lng, lat] : null,
      fw: String(r.fw || "").trim(),
    };
  }).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/** Pure: KPI strip for one device shell. */
export function fleetDeviceShellKpisOf(d, teleMeta) {
  const offline = !d || d.offline || d.online === false;
  return {
    online: offline ? 0 : 1,
    group: d && d.group ? 1 : 0,
    zone: d && d.zone ? 1 : 0,
    teleFresh: teleMeta && teleMeta.fresh ? 1 : 0,
  };
}

const TABS = [
  ["board", "Board"],
  ["overview", "Overview"],
  ["cameras", "Cameras"],
  ["forge", "Design a capability"],
  ["identity", "Identity"],
  ["place", "Site & Group"],
  ["caps", "Capabilities"],
  ["skills", "Skills warehouse"],
  ["net", "Link & power"],
  ["danger", "Custody"],
];

export function FleetDeviceDesk({ id, toast }) {
  const posture = useApi(path("get__fleet_device_status"));
  const post = posture.data || {};
  /* Fail-closed: only Live posture opens seats. No null invent board/custody. */
  const registerOk = posture.loading ? false : post.register_allowed === true;
  const boardOk = posture.loading ? false : (
    post.board_read_allowed === true || post.register_allowed === true
  );
  const writeOk = posture.loading ? false : (
    post.profile_write_allowed === true || post.write_allowed === true
  );
  const cmdsOk = posture.loading ? false : post.commands_allowed === true;
  const revokeOk = posture.loading ? false : post.revoke_allowed === true;
  const deleteOk = posture.loading ? false : post.delete_reset_allowed === true;
  const factoryOk = posture.loading ? false : (
    post.factory_unregister_allowed === true || post.revoke_allowed === true
  );
  const detail = post.detail || null;

  const devicesApi = useApi(registerOk ? path("get__devices") : "");
  const register = useMemo(() => fleetDeviceRegisterOf(devicesApi.data), [devicesApi.data]);
  const base = useMemo(
    () => register.find((row) => row.id === id) || null,
    [register, id],
  );
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("board");
  const [dirty, setDirty] = useState(false);
  const [confirmSerial, setConfirmSerial] = useState("");
  const act = useAction();
  const envelope = act.envelope;
  const softErr = (raw) => softFleetDeviceErrorOf(raw || act.error);
  const [pins, setPins] = useState(() => PINS.map((p) => ({ ...p })));
  const [ports, setPorts] = useState(() => geometryPortsSeed(PORTS));
  const [silicon, setSilicon] = useState(() =>
    SILICON_GEO.map((c) => ({ ...c, temp: null, live: null })));
  const [sel, setSel] = useState(null);
  const [therm, setTherm] = useState(false);
  const [card, setCard] = useState("mounted");
  const [probeBusy, setProbeBusy] = useState(false);
  const boardPins = useApi((boardOk && id) ? path("get__devices_p_pins", { pathParams: [id] }) : "");
  const boardPorts = useApi((boardOk && id) ? path("get__devices_p_ports", { pathParams: [id] }) : "");
  const boardTele = useApi((boardOk && id) ? path("get__devices_p_board-telemetry", { pathParams: [id] }) : "");

  useEffect(() => {
    if (!base) { setD(null); return; }
    if (!dirty) setD({ ...base });
  }, [base, dirty]);

  useEffect(() => {
    const items = (boardPins.data && (boardPins.data.items || boardPins.data.pins || boardPins.data.seats)) || [];
    let next = mergeLiveSeatsOntoSoftPins(PINS.map((p) => ({ ...p })), items);
    if (boardTele.data) next = applyPinTelemetry(next, boardTele.data);
    setPins(next);
  }, [boardPins.data, boardTele.data, id]);

  useEffect(() => {
    setPorts(mergeLivePortsOntoSoftPorts(geometryPortsSeed(PORTS), boardPorts.data || null));
  }, [boardPorts.data, id]);

  useEffect(() => {
    const geo = SILICON_GEO.map((c) => ({ ...c, temp: null, live: null }));
    setSilicon(applySiliconTelemetry(geo, boardTele.data || null));
  }, [boardTele.data, id]);

  const poe = useMemo(() => poeLiveFromTelemetry(boardTele.data || null), [boardTele.data]);
  const teleMeta = useMemo(() => ({
    fresh: !!(boardTele.data && boardTele.data.fresh),
    age_s: boardTele.data && boardTele.data.age_s != null ? Number(boardTele.data.age_s) : null,
    loading: !!boardTele.loading,
    error: boardTele.error || null,
    portsError: boardPorts.error || null,
    portsLoading: !!boardPorts.loading,
  }), [boardTele.data, boardTele.loading, boardTele.error, boardPorts.error, boardPorts.loading]);

  const kpis = useMemo(() => fleetDeviceShellKpisOf(d, teleMeta), [d, teleMeta]);

  const reloadBoard = () => {
    boardPins.reload && boardPins.reload();
    boardPorts.reload && boardPorts.reload();
    boardTele.reload && boardTele.reload();
  };

  const reRead = () => {
    posture.reload && posture.reload();
    devicesApi.reload && devicesApi.reload();
    reloadBoard();
    toast && toast("Device status refreshed", "go");
  };

  const requestTelemetry = async () => {
    if (!id) { toast && toast("No unit selected", "warn"); return; }
    if (!cmdsOk) {
      toast && toast("Commands closed for this seat", "warn");
      return;
    }
    setProbeBusy(true);
    try {
      const out = await act.run(
        path("post__devices_p_commands", { pathParams: [id] }),
        { method: "POST", body: { cmd: "ping", params: {} } },
      );
      if (!out) {
        toast && toast(softErr(), "bad");
        return;
      }
      toast && toast("Check-in requested · refreshing board", "go");
      reloadBoard();
    } finally {
      setProbeBusy(false);
    }
  };

  if (!posture.loading && post.enabled !== true) {
    return html`<div className="fleet-device-desk fleet-device-desk--liquid"
      data-cmc-page="page136-device" data-cmc-legacy="page74-device"
      data-cmc-residual="page174-device">
      <div className="fleet-device-desk__rail">
        <div>
          <p className="eyebrow">Machine</p>
          <h2 className="fleet-device-desk__title">Seat closed</h2>
          <p className="note">Your seat cannot open device board records on this grant.</p>
        </div>
        <div className="btn-row fleet-device-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Refresh</button>
        </div>
      </div>
      <${Card} title="Device posture" meta="closed">
        <p className="note">${post.posture_label || "Device board closed for this seat"} · role ${post.principal_role || "—"}</p>
      <//>
    </div>`;
  }

  if (registerOk && devicesApi.loading && !register.length) {
    return html`<div className="fleet-device-desk fleet-device-desk--liquid"
      data-cmc-page="page136-device" data-cmc-legacy="page74-device"
      data-cmc-residual="page174-device">
      <${Alert} tone="info" icon="info" title="Reading the device register"
        say="Reading devices on your account…"/>
    </div>`;
  }

  if (registerOk && (!base || !d)) {
    return html`<div className="fleet-device-desk fleet-device-desk--liquid"
      data-cmc-page="page136-device" data-cmc-legacy="page74-device"
      data-cmc-residual="page174-device">
      <${Card} title="Device posture" meta="status">
        <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
          <${KV} k="register" v=${registerOk ? "open" : "closed"}/>
          <${KV} k="profile write" v=${writeOk ? "open" : "closed"}/>
          <${KV} k="move board" v=${revokeOk ? "open" : "closed"}/>
        </div>
      <//>
      <${Alert} tone="bad" icon="ban" title="Unit not on this account"
        say="Nothing is registered under that serial for you. Empty desks stay empty."/>
      <div className="btn-row fleet-device-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/fleet/groups")}>
          Fleet groups</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Refresh</button>
      </div>
    </div>`;
  }

  if (!d) return null;

  const set = (k, v) => {
    if (!writeOk) {
      toast && toast("Profile edit closed for this seat", "warn");
      return;
    }
    setD((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };

  const savePlace = async () => {
    if (!writeOk) {
      toast && toast("Place edit closed for this seat", "warn");
      return;
    }
    const body = {
      display_label: String(d.label || "").trim(),
      notification_group: d.group === "Unassigned" ? "" : canonicalGroupKey(d.group),
    };
    if (Array.isArray(d.ll) && d.ll.length >= 2) {
      const lng = Number(d.ll[0]);
      const lat = Number(d.ll[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)
          && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        body.map_lat = lat;
        body.map_lng = lng;
      }
    }
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [d.id] }), {
      method: "PATCH",
      body,
    });
    if (out) {
      setDirty(false);
      toast && toast("Place saved", "go");
      devicesApi.reload && devicesApi.reload();
    } else toast && toast(softErr(), "bad");
  };

  const k = kindOf(d.kind);
  const offline = !!d.offline || d.online === false;
  const ageText = fleetDeviceAgeOf(d.age);
  const regErr = devicesApi.error;

  return html`<div className="fleet-device-desk fleet-device-desk--liquid desk-craft"
    data-cmc-page="page136-device" data-cmc-legacy="page74-device"
    data-cmc-residual="page174-device">
    <div className="fleet-device-desk__rail">
      <div>
        <p className="eyebrow">Machine</p>
        <h2 className="fleet-device-desk__title">${d.label}</h2>
        <p className="note fleet-device-desk__lede">
          Board · place · custody · capabilities · skills.
          Function OTA on Skills tab · board OTA never from this desk.
        </p>
      </div>
      <div className="btn-row fleet-device-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act" onClick=${reRead}
          disabled=${posture.loading || devicesApi.loading}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => setTab("skills")}>Skills warehouse</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/edge/skills")}>Edge warehouse</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/ota/base")}>Board updates</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act" onClick=${() => go("/ops")}>
          Ops map</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/fleet/groups")}>Groups</button>
      </div>
    </div>

    <${Card} title="Device posture" meta="status">
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="register" v=${registerOk ? "open" : "closed"}/>
        <${KV} k="board" v=${boardOk ? "open" : "closed"}/>
        <${KV} k="profile write" v=${writeOk ? "open" : "closed"}/>
        <${KV} k="move board" v=${revokeOk ? "open" : "closed"}/>
      </div>
      ${detail ? html`<p className="note" style=${{ marginTop: 8 }}>detail · ${detail}</p>` : null}
    <//>

    <${Alert} tone="info" title="Singular machine desk"
      say="Open this unit for overview, hardware, and diagnostics. Empty seats stay empty — no invented boards."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${regErr ? html`
      <${Alert} tone="warn" title="Register partial"
        say=${softFleetDeviceErrorOf(regErr)}/>` : null}

    <div className="fleet-device-desk__kpis" role="group" aria-label="Device KPIs">
      ${[
        ["Presence", offline ? 0 : 1, offline ? "0" : "1", offline ? "off" : ""],
        ["Group", kpis.group, kpis.group ? "1" : "0", ""],
        ["Zone", kpis.zone, kpis.zone ? "1" : "0", ""],
        ["Telemetry", kpis.teleFresh, kpis.teleFresh ? "1" : "0", ""],
      ].map(([lab, v, on, tone]) => html`
        <div key=${lab} className="fleet-device-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${lab}</span>
          <b>${lab === "Presence" ? (offline ? "quiet" : "live") : (lab === "Telemetry" ? (kpis.teleFresh ? "fresh" : "—") : (v ? "yes" : "—"))}</b>
          <em>Live</em>
        </div>`)}
    </div>

    <div className="dhead glass fleet-device-desk__head">
      <span className="dhead__ic" style=${{ "--kc": k.tone }}><${Ico} d=${k.glyph} w=${20}/></span>
      <span className="dhead__t">
        <h1 style=${{ margin: 0, fontSize: "var(--t-lead)" }}>${d.label}</h1>
        <em>${k.t} · <code>${d.id}</code> · ${d.zone || "—"} · ${d.group || "no group"}</em>
      </span>
      <span className="dhead__st" data-off=${offline ? "1" : "0"}>
        <i/>${offline ? "not answering" : ("answered " + ageText)}</span>
    </div>

    <div className="dtabs fleet-device-desk__tabs">
      ${TABS.map(([tid, l]) => html`
        <button key=${tid} type="button" className="fleet-device-desk__tab"
          data-on=${tab === tid ? "1" : "0"} data-tab=${tid}
          onClick=${() => setTab(tid)}>
          ${tab === tid && html`<${M.span} layoutId="dtab" className="nav-pill" transition=${SPRING}/>`}
          <span>${l}</span>
        </button>`)}
    </div>

    <${AnimatePresence} mode="wait">
      <${M.div} key=${tab} initial=${{ opacity: 0, y: 8 }} animate=${{ opacity: 1, y: 0 }}
          exit=${{ opacity: 0, y: -6 }} transition=${FADE}
          style=${{ display: "flex", flexDirection: "column", gap: 14 }}>

        ${tab === "board" && html`
          <${F}>
            ${boardPins.error && !boardPins.data ? html`
              <${Alert} tone="warn" title="Live board seats unavailable"
                say=${boardPins.error}/>` : null}
            ${boardPorts.error && !boardPorts.data ? html`
              <${Alert} tone="warn" title="Live ports unavailable"
                say=${boardPorts.error}/>` : null}
            <div className="bwrap">
              <div className="bwrap__stage glass">
                <${Board3D} deviceId=${d.id} pins=${pins} setPins=${setPins} ports=${ports}
                  silicon=${silicon}
                  sel=${sel} setSel=${setSel} therm=${therm} setTherm=${setTherm}
                  card=${card} onCard=${setCard} toast=${toast} onReload=${reloadBoard}
                  onRequestTelemetry=${requestTelemetry}
                  probeBusy=${probeBusy || act.busy}
                  teleMeta=${teleMeta}
                  boardLoading=${boardPins.loading || boardPorts.loading || boardTele.loading}/>
              </div>
              <div className="bwrap__side glass">
                <${BoardDetail} deviceId=${d.id} sel=${sel} pins=${pins} setPins=${setPins}
                  ports=${ports} silicon=${silicon} card=${card} onCard=${setCard} toast=${toast}
                  onReload=${reloadBoard}/>
              </div>
            </div>
            <${Silicon} therm=${therm} pins=${pins} silicon=${silicon} poe=${poe} teleMeta=${teleMeta}/>
          <//>`}

        ${tab === "cameras" && html`<${DeviceCamerasLive} deviceId=${d.id} toast=${toast}/>`}

        ${tab === "forge" && html`
          <${Forge} pins=${pins} setPins=${setPins} ports=${ports} device=${d} toast=${toast}/>`}

        ${tab === "overview" && html`
          <${DeviceOverviewLive}
            deviceId=${d.id}
            device=${d}
            offline=${offline}
            commandAllowed=${cmdsOk}
            toast=${toast}/>`}

        ${tab === "identity" && html`
          <${DeviceIdentityLive}
            deviceId=${d.id}
            device=${d}
            kinds=${KINDS_DEVICE}
            kindOf=${kindOf}
            toast=${toast}
            onLabel=${(lab) => { setD((x) => ({ ...x, label: lab })); setDirty(false); }}/>`}

        ${tab === "place" && html`
          <div className="grid g2">
            <${Card} title="Zone" meta="Ring membership">
              <${Alert} tone="info" title="Zone is not a free text label"
                say="Live zone comes from the device register. Draw rings on Zones; membership follows map geometry."/>
              <${KV} k="Live zone" v=${d.zone || "—"} tone=${d.zone ? "" : "dim"}/>
              <div className="btn-row" style=${{ marginTop: 10 }}>
                <button className="btn btn--ghost btn--sm" type="button" onClick=${() => go("/fleet/zones")}>
                  Draw or edit zones</button>
              </div>
            <//>
            <${Card} title="Group" meta="who wakes with it">
              <p className="sub">Siblings share this string under the same owner. Same name under different owners are not siblings.</p>
              <div className="opts">
                ${[...new Set(register.map((x) => canonicalGroupKey(x.group)).filter(Boolean))].map((g) => html`
                  <button key=${g} type="button" className="opt" data-on=${sameGroupKey(d.group, g) ? "1" : "0"}
                    onClick=${() => set("group", g)}>
                    <b>${g}</b><em>${register.filter((x) => sameGroupKey(x.group, g)).length} devices</em></button>`)}
              </div>
              <div className="btn-row fleet-device-desk__acts">
                <button className="btn btn--ghost btn--sm" type="button" onClick=${() => go("/fleet/groups")}>
                  Edit membership</button>
              </div>
            <//>
          </div>
          <${Card} title="Surveyed position" meta="Map pin · live write">
            <div className="grid g2" style=${{ gap: "0 16px" }}>
              <${Field} label="Longitude"><${Input} icon="map"
                value=${Array.isArray(d.ll) && d.ll[0] != null ? d.ll[0] : ""}
                onChange=${(e) => {
                  const lng = Number(e.target.value);
                  const lat = Array.isArray(d.ll) && Number.isFinite(Number(d.ll[1])) ? Number(d.ll[1]) : 0;
                  set("ll", [Number.isFinite(lng) ? lng : 0, lat]);
                }}/><//>
              <${Field} label="Latitude"><${Input} icon="map"
                value=${Array.isArray(d.ll) && d.ll[1] != null ? d.ll[1] : ""}
                onChange=${(e) => {
                  const lat = Number(e.target.value);
                  const lng = Array.isArray(d.ll) && Number.isFinite(Number(d.ll[0])) ? Number(d.ll[0]) : 0;
                  set("ll", [lng, Number.isFinite(lat) ? lat : 0]);
                }}/><//>
            </div>
            <p className="note">Save writes the map pin on the live registry. Edits that never left the browser stay discarded when you leave.</p>
            <div className="btn-row" style=${{ marginTop: 12 }}>
              <button className="btn btn--go btn--sm" type="button"
                disabled=${!writeOk || !dirty || act.busy}
                onClick=${savePlace}>
                ${act.busy ? "Writing…" : "Save place"}</button>
              <button className="btn btn--ghost btn--sm" type="button"
                disabled=${!dirty}
                onClick=${() => { setD({ ...base }); setDirty(false); toast && toast("Draft discarded", "warn"); }}>
                Discard draft</button>
            </div>
          <//>`}

        ${tab === "caps" && html`<${DeviceCapsLive} deviceId=${d.id} toast=${toast}/>`}
        ${tab === "skills" && html`<${EdgeSkillsWarehouseDesk}
          mode="device" deviceId=${d.id} toast=${toast}
          seat=${{
            surveyAllowed: registerOk,
            commandAllowed: writeOk || cmdsOk,
            packagePushAllowed: writeOk || cmdsOk,
            reReadAllowed: registerOk,
          }}/>`}

        ${tab === "net" && html`
          <${DeviceLinkPowerLive} deviceId=${d.id} device=${d} register=${register}
            commandAllowed=${cmdsOk} toast=${toast}/>`}

        ${tab === "danger" && html`
          <${Card} title="Custody" meta="irreversible acts need serial confirm">
            <${Alert} tone="warn" icon="alert" title="Two of these cannot be undone from here"
              say="Releasing returns the unit to your unclaimed pool work. Unregistering returns the serial to the factory pool. Type the serial below before those buttons arm."/>
            <${Field} label="Type this unit serial to confirm release or unregister">
              <${Input} icon="chip" placeholder=${d.id} value=${confirmSerial}
                onChange=${(e) => setConfirmSerial(e.target.value)} autocomplete="off"/>
            <//>
            <div className="dacts">
              <button type="button" className="dact" disabled=${!revokeOk || act.busy}
                onClick=${async () => {
                if (!revokeOk) { toast && toast("Custody change closed for this seat", "warn"); return; }
                const out = await act.run(path("post__devices_p_revoke", { pathParams: [d.id] }), {
                  method: "POST",
                  body: { reason: "Fleet device desk · revoke publish credentials" },
                });
                if (out) toast && toast("Publish credentials revoked · ownership unchanged", "warn");
                else toast && toast(softErr(), "bad");
              }}>
                <b>Revoke publish credentials</b><code>${revokeOk ? "reversible" : "seat closed"}</code></button>
              <button type="button" className="dact" onClick=${() => go("/sa/custody")}>
                <b>Move to another site</b><code>Site transfer</code></button>
              <button type="button" className="dact" data-bad="1"
                disabled=${!deleteOk || act.busy || confirmSerial.trim() !== d.id}
                onClick=${async () => {
                if (!deleteOk) { toast && toast("Release closed for this seat", "warn"); return; }
                if (confirmSerial.trim() !== d.id) {
                  toast && toast("Type the full unit serial exactly to confirm", "warn"); return;
                }
                const out = await act.run(path("post__devices_p_delete-reset", { pathParams: [d.id] }), {
                  method: "POST",
                  body: { confirm_text: d.id },
                });
                if (out) {
                  toast && toast("Release requested from this account", "warn");
                  setConfirmSerial("");
                } else toast && toast(softErr(), "bad");
              }}>
                <b>Release from this account</b><code>${deleteOk ? "irreversible" : "seat closed"}</code></button>
              <button type="button" className="dact" data-bad="1"
                disabled=${!factoryOk || act.busy || confirmSerial.trim() !== d.id}
                onClick=${async () => {
                if (!factoryOk) { toast && toast("Factory unregister closed for this seat", "warn"); return; }
                if (confirmSerial.trim() !== d.id) {
                  toast && toast("Type the full unit serial exactly to confirm", "warn"); return;
                }
                const out = await act.run(path("post__devices_p_factory-unregister", { pathParams: [d.id] }), {
                  method: "POST",
                  body: { confirm_text: d.id },
                });
                if (out) {
                  toast && toast("Serial returned to factory pool", "warn");
                  setConfirmSerial("");
                } else toast && toast(softErr(), "bad");
              }}>
                <b>Unregister the serial</b><code>${factoryOk ? "managers only" : "seat closed"}</code></button>
            </div>
          <//>`}
      <//>
    <//>
  </div>`;
}

export function Device({ id, toast }) {
  return html`<${FleetDeviceDesk} id=${id} toast=${toast}/>`;
}
